import {
    FC,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import classNames from 'classnames';

import { Icon } from '../../components';
import {
    Button,
    Dialog,
    DialogContent,
    LinkIconButton,
    ShoppingCartCount,
} from '../../compositions';
import { ExtendedOrderInterface } from '../../entities/@shop/Order/Order';
import { HorizontalAlignment } from '../../entities/Alignment/Alignment';
import { AppRoute, appRoutes } from '../../entities/Routing/Routing';
import { ToastInterface } from '../../entities/Toast/Toast';
import { useDeviceWidth, useTrans } from '../../hooks';
import { AtLeast } from '../../types';
import { CartItemForm } from '../@forms/CartItemForm/CartItemForm';
import { CartItemFormSkeletons } from '../@forms/CartItemForm/skeletons';
import { ProductQuantitySubmitFormData } from '../@forms/CartItemForm/validations';
import { Pricing } from '../Checkout/subcomponents';

import './CartSidebar.scss';

interface CartSidebarProps extends ExtendedOrderInterface {
    isInitiallyLoading: boolean;
    isLoading: boolean;
    productIsAddedToCart: boolean;
    wishlistItems: string[];
    onShowMobileProductIsAddedToCart: (toast: AtLeast<ToastInterface, 'title'>) => void;
    onUpdateCartItem: (formData: ProductQuantitySubmitFormData) => void;
    onUpdateCartItemIsInWishlist: (productId: string, isInWishlist: boolean) => void;
    onRemoveItemFromCart: (productId: string) => void;
    onResetProductIsAddedToCart: () => void;
    className?: string;
}

export const CartSidebar: FC<CartSidebarProps> = ({
    isInitiallyLoading,
    isLoading,
    productIsAddedToCart,
    items,
    subTotalPrice,
    handlingPrice,
    totalPrice,
    discounts,
    uspItems,
    wishlistItems,
    onShowMobileProductIsAddedToCart,
    onUpdateCartItem,
    onUpdateCartItemIsInWishlist,
    onRemoveItemFromCart,
    onResetProductIsAddedToCart,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { isMobile } = useDeviceWidth();

    const dialogRef = useRef<HTMLDialogElement>(null);

    const [shouldHide, setShouldHide] = useState<boolean>(false);
    const [layoutIsMini, setLayoutIsMini] = useState<boolean>(false);

    const totalItemsInCart = items.reduce((acc, item) => acc + item.quantity, 0);

    const handleOpenDialog = (): void => {
        if (!dialogRef.current) {
            return;
        }

        dialogRef.current.showModal();
    };

    const handleOpenMiniSideBar = (): void => {
        setLayoutIsMini(true);
        handleOpenDialog();
    };

    useEffect((): void => {
        if (productIsAddedToCart && isMobile) {
            onShowMobileProductIsAddedToCart({
                title: trans('containers.cartSidebar.productIsAddedToCartTitle'),
                description: trans('containers.cartSidebar.productIsAddedToCartDescription'),
            });

            return;
        }

        if (productIsAddedToCart) {
            handleOpenMiniSideBar();
        }
    }, [productIsAddedToCart]);

    const handleCloseDialog = (): void => {
        if (!dialogRef.current) {
            return;
        }

        dialogRef.current.close();
        setShouldHide(false);

        if (layoutIsMini) {
            setLayoutIsMini(false);
        }

        onResetProductIsAddedToCart();
    };

    const handleCloseDialogByClick = (): void => {
        setShouldHide(true);
    };

    const dialogClassNames = classNames('cart-sidebar__dialog', {
        'cart-sidebar__dialog--layout-mini': layoutIsMini,
    });

    return (
        <div className={`cart-sidebar ${className}`}>
            <ShoppingCartCount
                isLoading={isLoading}
                count={totalItemsInCart}
                onClick={handleOpenDialog}
            />

            <Dialog
                ref={dialogRef}
                enableBackdropClose
                hasDarkBackdrop={!layoutIsMini}
                onClose={handleCloseDialog}
                shouldHide={shouldHide}
                className={dialogClassNames}
            >
                <DialogContent>
                    <h6 className="cart-sidebar__heading">
                        {trans('common.cart')}
                    </h6>

                    {isInitiallyLoading && (
                        <CartItemFormSkeletons className="cart-sidebar__skeletons" />
                    )}

                    <div className="cart-sidebar__items-wrapper">
                        {items.map(item => (
                            <CartItemForm
                                key={item.id}
                                {...item}
                                showWishlistButton
                                isEditMode
                                isLoading={isLoading}
                                wishlistItems={wishlistItems}
                                onClickToProductPage={handleCloseDialogByClick}
                                onUpdateCartItem={onUpdateCartItem}
                                onUpdateCartItemIsInWishlist={onUpdateCartItemIsInWishlist}
                                onRemoveItemFromCart={onRemoveItemFromCart}
                                className="cart-sidebar__item"
                            />
                        ))}

                        {!isInitiallyLoading && !isLoading && items.length === 0 && (
                            <div className="cart-sidebar__no-products-wrapper">
                                <Icon name="cart" className="cart-sidebar__no-products-icon" />

                                <p className="cart-sidebar__no-products-text">
                                    {trans('containers.cartSidebar.noProductsInCart')}
                                </p>
                            </div>
                        )}
                    </div>

                    {items.length > 0 && (
                        <Pricing
                            hideCouponCodeInput
                            isLoading={isLoading}
                            subTotalPrice={subTotalPrice}
                            handlingPrice={handlingPrice}
                            totalPrice={totalPrice}
                            discounts={discounts}
                            className="cart-sidebar__pricing"
                            totalPriceClassName="cart-sidebar__pricing-total-price"
                        />
                    )}

                    <div className="cart-sidebar__buttons-wrapper">
                        {items.length > 0 && (
                            <LinkIconButton
                                isSmall
                                hasAnimation
                                icon="arrow-right"
                                iconPos={HorizontalAlignment.right}
                                text={trans('common.toCart')}
                                to={trans(appRoutes[AppRoute.checkoutOrder].path)}
                                onClick={handleCloseDialogByClick}
                                className="cart-sidebar__button"
                            />
                        )}

                        {!layoutIsMini && (
                            <Button
                                isSmall
                                hasAnimation
                                text={trans('common.continueShopping')}
                                onClick={handleCloseDialogByClick}
                                className="cart-sidebar__button cart-sidebar__button--is-outline"
                            />
                        )}
                    </div>

                    {!layoutIsMini && (
                        <ul className="cart-sidebar__usp-list">
                            {uspItems.map(item => (
                                <li
                                    key={item.id}
                                    className="cart-sidebar__usp-item"
                                >
                                    {item.icon && (
                                        <Icon name={item.icon} className="cart-sidebar__usp-item-icon" />
                                    )}

                                    {item.text}
                                </li>
                            ))}
                        </ul>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
